import React, { forwardRef, useMemo } from 'react';

import { Box } from '@chakra-ui/react';
import { useMediaQuery } from '@material-ui/core';

import { mobileDownMediaQuery } from 'constants/helper';

import { AgGridWrapper } from '../AgGridWrapper';
import { type AgGridWrapperProps } from '../AgGridWrapper/AgGridWrapper';
import './styles/AgGridStyles.css';
import { AgGridKebabMenuOptions } from './types';
import { appendSidebarToggler } from './utils';

type AgGirdProps =
  | {
      showSidebarToggle?: boolean;
      kebabMenuOptions?: AgGridKebabMenuOptions;
      // very specific to OKR, as it is the only place where we have row specific kebab menu options
      kebabMenuIsRowSpecific?: true;
      kebabMenuOptionsGenerator: (params: any) => AgGridKebabMenuOptions;
      isTreeView?: boolean;
      isMobileView?: boolean;
      containerAutoHeight?: boolean;
      additionalMobileProps?: any;
      isOverviewPage?: boolean;
      minHeight?: string;
      height?: string;
    }
  | {
      showSidebarToggle?: boolean;
      kebabMenuOptions?: AgGridKebabMenuOptions;
      kebabMenuIsRowSpecific: false;
      isTreeView?: boolean;
      isMobileView?: boolean;
      containerAutoHeight?: boolean;
      additionalMobileProps?: any;
      isOverviewPage?: boolean;
      minHeight?: string;
      height?: string;
    };

export type AgGridTableProps = AgGridWrapperProps & AgGirdProps;

const AgGridTable = forwardRef<typeof AgGridWrapper, AgGridTableProps>((props, ref) => {
  const {
    columnDefs = [],
    sideBar,
    kebabMenuOptions,
    kebabMenuIsRowSpecific,
    getUIOptionsForLastColumn,
    isTreeView,
    isOverviewPage = false,
  } = props ?? {};
  const {
    containerAutoHeight,
    showSidebarToggle = true,
    autoGroupColumnDef,
    additionalMobileProps = {},
    minHeight = '250px',
    height = 'calc(100vh - 300px)',
  } = props ?? {};

  const isMobileView = useMediaQuery(mobileDownMediaQuery);

  const kebabOptions: AgGirdProps = useMemo(() => {
    if (kebabMenuIsRowSpecific) {
      return {
        kebabMenuIsRowSpecific: true,
        // @ts-ignore
        kebabMenuOptionsGenerator: props.kebabMenuOptionsGenerator,
      };
    }

    return {
      kebabMenuIsRowSpecific: false,
    };

    // don't mess up with this dependency array, it introduces infinite loop if you don't know what you are doing
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kebabMenuIsRowSpecific]);

  const preparedColDefs = useMemo(() => {
    // checking for discriminator property
    if ('kebabMenuIsRowSpecific' in kebabOptions && kebabOptions.kebabMenuIsRowSpecific) {
      return appendSidebarToggler({
        colDefs: columnDefs,
        kebabMenuOptions,
        kebabMenuIsRowSpecific: kebabOptions.kebabMenuIsRowSpecific,
        kebabMenuOptionsGenerator: kebabOptions.kebabMenuOptionsGenerator,
        isMobileView,
        showSidebarToggle,
        autoGroupColumnDef,
        getUIOptionsForLastColumn,
        additionalMobileProps,
      });
    }
    if (isOverviewPage) {
      return appendSidebarToggler({
        colDefs: columnDefs,
        kebabMenuOptions,
        isMobileView,
        autoGroupColumnDef,
        additionalMobileProps,
      });
    }
    return appendSidebarToggler({
      colDefs: columnDefs,
      kebabMenuOptions,
      isMobileView,
      showSidebarToggle,
      autoGroupColumnDef,
      additionalMobileProps,
    });
    // don't mess up with this dependency array, it introduces infinite loop if you don't know what you are doing
  }, [columnDefs, kebabMenuOptions, kebabOptions, isMobileView]);

  const preparedSideBar = useMemo(() => {
    if (['string', 'boolean'].includes(typeof sideBar) || !sideBar) {
      return sideBar;
    }
    if (typeof sideBar === 'object' && sideBar !== null) {
      return {
        ...sideBar,
        hiddenByDefault: true,
      };
    }
  }, [sideBar]);

  const preparedAutoGroupColumnDef = useMemo(() => {
    const mobileProps = isMobileView
      ? {
          pinned: 'left',
          lockPosition: 'left',
          ...additionalMobileProps,
        }
      : {};
    if (autoGroupColumnDef) {
      return {
        ...autoGroupColumnDef,
        ...mobileProps,
      };
    }
  }, [autoGroupColumnDef, isMobileView]);

  return (
    <Box
      className={`ag-theme-alpine goals-ag-grid hide-sidebar-buttons ${isMobileView ? 'mobile-style' : ''}`} // to migrate goals-ag-grid to generic css class
      style={{
        width: '100%',
        height: containerAutoHeight ? 'auto' : height,
        minHeight,
      }} // <- need to discuss height and width with design
    >
      {/* TODO: When migrating this to rabbit repo, make sure to make the wrapper a generic type so that we can pass TData< > */}
      <AgGridWrapper
        //   @ts-ignore
        ref={ref}
        {...props}
        columnDefs={preparedColDefs}
        sideBar={preparedSideBar}
        // @ts-ignore
        autoGroupColumnDef={preparedAutoGroupColumnDef}
        suppressGroupRowsSticky
      />
    </Box>
  );
});

export { AgGridTable };
